/**
 * Browser-Side Stellungnahme-Renderer.
 *
 * Lädt die Word-Vorlage (`apps/web/public/templates/Stellungnahme_B2G.docx`),
 * wendet die Platzhalter-Map (aus `@tragfaehigkeit/core/statement`) per
 * docxtemplater an und gibt einen Blob für Download zurück.
 *
 * Das Template nutzt französische Anführungszeichen `«…»` als Delimiter —
 * docxtemplater wird entsprechend konfiguriert.
 *
 * Sicherheit (DSGVO):
 *   - Die Generierung läuft komplett im Browser. Mandantendaten verlassen
 *     nicht den Endgerät.
 *   - Das fertige DOCX bleibt im Memory bis zum Download — keine Persistenz.
 */

import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import type { statement } from '@tragfaehigkeit/core';

type StatementPlaceholders = statement.StatementPlaceholders;

const TEMPLATE_URL = '/templates/Stellungnahme_B2G.docx';

let templateBufferPromise: Promise<ArrayBuffer> | null = null;

async function fetchTemplateBuffer(): Promise<ArrayBuffer> {
  if (!templateBufferPromise) {
    templateBufferPromise = fetch(TEMPLATE_URL).then(async (res) => {
      if (!res.ok) {
        throw new Error(
          `renderStatementDocx: Template "${TEMPLATE_URL}" konnte nicht geladen werden (HTTP ${String(res.status)}).`,
        );
      }
      return res.arrayBuffer();
    });
  }
  return templateBufferPromise;
}

export interface RenderOptions {
  /** Optional: Dateiname-Vorschlag für den Download. */
  readonly filename?: string;
}

export interface RenderResult {
  readonly blob: Blob;
  readonly filename: string;
}

/**
 * Rendert eine Stellungnahme als DOCX-Blob.
 *
 * @throws Error wenn das Template fehlt oder docxtemplater einen
 *         Platzhalter nicht auflösen kann.
 */
export async function renderStatementDocx(
  placeholders: StatementPlaceholders,
  opts: RenderOptions = {},
): Promise<RenderResult> {
  const buffer = await fetchTemplateBuffer();
  const zip = new PizZip(buffer);

  const doc = new Docxtemplater(zip, {
    paragraphLoop: true,
    linebreaks: true,
    delimiters: { start: '«', end: '»' },
    nullGetter: () => '', // unbekannte Platzhalter werden zu leerem String
  });

  doc.render(placeholders);

  const out = doc.getZip().generate({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    compression: 'DEFLATE',
  });

  const fallbackName = `Stellungnahme_${(placeholders['CASE_ID'] ?? 'Mandant').replace(/\W+/g, '_')}.docx`;

  return { blob: out, filename: opts.filename ?? fallbackName };
}

/**
 * Convenience: rendert + triggert sofort den Download im Browser.
 * Räumt die temporäre Object-URL nach 1s auf.
 */
export async function renderAndDownloadStatement(
  placeholders: StatementPlaceholders,
  opts: RenderOptions = {},
): Promise<void> {
  const { blob, filename } = await renderStatementDocx(placeholders, opts);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
}
